import { useMemo } from "react";
import { useLocale } from "@acto/web-common/locales";
import dayjs from "dayjs";
import type { TodoType } from "@acto/gql/graphql";
import { useEmployeeTodos } from "./useEmployeeTodos";
import type { TodoSummary } from "@acto/taskpane/core/domain/TeamEmployee";

interface TodoItem {
  id: string;
  date: string;
  title: string;
  description: string;
  clientId: string | null;
  clientName: string | null;
  clientExternalReference: string | null;
  clientAccessible: boolean;
}

export interface OpenTodosData {
  employeeName: string;
  overdueTodos: {
    count: number;
    items: TodoItem[];
  };
  openTodos: {
    count: number;
    items: TodoItem[];
  };
  isPending: boolean;
}

interface UseOpenTodosDataOptions {
  employeeId: string | null;
  employeeName: string;
  todoTypes: TodoType[];
}

export function useOpenTodosData({
  employeeId,
  employeeName,
  todoTypes,
}: UseOpenTodosDataOptions): OpenTodosData | null {
  const { locale } = useLocale();

  const { todos, isPending } = useEmployeeTodos({ employeeId, todoTypes });

  return useMemo(() => {
    if (!employeeId) return null;

    const today = dayjs().startOf("day");

    const overdue = todos.filter((t) => t.dueDate && t.dueDate.isBefore(today));
    const open = todos.filter((t) => !t.dueDate || !t.dueDate.isBefore(today));

    const toItem = (todo: TodoSummary): TodoItem => ({
      id: todo.id,
      date: (todo.dueDate ?? todo.createdAt)?.locale(locale).format("D MMM") ?? "",
      title: todo.title,
      description: todo.description,
      clientId: todo.clientId,
      clientName: todo.clientName,
      clientExternalReference: todo.clientExternalReference,
      clientAccessible: todo.clientAccessible,
    });

    return {
      employeeName,
      overdueTodos: {
        count: overdue.length,
        items: overdue.map(toItem),
      },
      openTodos: {
        count: open.length,
        items: open.map(toItem),
      },
      isPending,
    };
  }, [employeeId, employeeName, todos, locale, isPending]);
}
